import { loadGoogleMapsScript } from "@/utils/loadGoogleMapsScript";

/**
 * One formatted address in, one point out — or an honest account of why not.
 *
 * WHY THIS IS NOT `Promise<{lat,lng} | null>`. The caller has to tell two
 * answers apart that a null collapses into one: the geocoder REPLIED and knows
 * nothing about this address, versus the lookup never happened (no key, the
 * script failed, the network died). The first is a fact about the address and
 * is worth saying out loud on screen; the second is our problem and must not
 * be reported to a professional as "we could not find your client's street".
 * Spec 2f3eb584, control C-3.
 *
 * WHY A GEOCODER AT ALL, when the consumer's own address picker has exact
 * coordinates: Places Autocomplete hands those back at pick time, and nothing
 * stores them — `user_address` has latitude/longitude, the delivery SNAPSHOT
 * on quote_request does not, and the professional's payload carries neither.
 * Geocoding the snapshotted text is what makes this a component change rather
 * than a migration. Its weakness is recorded as residue RES-1: a free-text
 * lote address on a rural camino is exactly where this drifts.
 */
/** Long enough for a slow network, short enough that a denied key is not a hang. */
const GEOCODE_TIMEOUT_MS = 8000;

export type GeocodeResult =
  | { status: "located"; lat: number; lng: number }
  | { status: "not_found" }
  | { status: "failed" };

/**
 * `google.maps.Geocoder` lives in the CORE Maps JS API — it needs no entry in
 * the `libraries` parameter, which is why this reuses the loader the address
 * picker already calls rather than requesting a second script with different
 * parameters. Verified 2026-10-19 (spec thesis T-1).
 */
export async function geocodeAddress(
  address: string,
  apiKey: string,
): Promise<GeocodeResult> {
  const query = address.trim();
  if (query === "" || apiKey.trim() === "") return { status: "failed" };

  try {
    await loadGoogleMapsScript(apiKey);
  } catch {
    return { status: "failed" };
  }

  const maps = (globalThis as typeof globalThis & { google?: typeof google }).google?.maps;
  if (!maps?.Geocoder) return { status: "failed" };

  return new Promise<GeocodeResult>((resolve) => {
    /*
      A CALLBACK THAT NEVER FIRES IS THE REAL FAILURE MODE HERE, and it is not
      hypothetical: probed 2026-10-19 against a referrer-restricted key, the
      script LOADS, `google.maps.Geocoder` exists, geocode() is called — and
      the callback is never invoked at all (RefererNotAllowedMapError goes to
      the console instead). Without this timer the promise never settles and
      the card sits on "Ubicando la dirección…" forever, which is a worse
      answer than saying the map is unavailable.
    */
    let settled = false;
    const finish = (result: GeocodeResult): void => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    const timer = setTimeout(() => finish({ status: "failed" }), GEOCODE_TIMEOUT_MS);
    const resolveAndClear = (result: GeocodeResult): void => {
      clearTimeout(timer);
      finish(result);
    };
    try {
      void new maps.Geocoder().geocode({ address: query }, (results, status) => {
        /*
          COMPARED AS A STRING, AND THAT IS CHECKED, not assumed: `status` is
          typed `google.maps.GeocoderStatus`, which is a STRING ENUM whose
          members are their own literals (`ZERO_RESULTS = 'ZERO_RESULTS'`,
          @types/google.maps index.d.ts:2284), and the API hands back those
          strings at runtime. Both this form and the enum form type-check.
          Worth stating because if it were a numeric enum this branch would
          never fire and "not found" would silently become "failed" — which is
          exactly the distinction control C-3 exists to keep.

          ZERO_RESULTS is the geocoder ANSWERING. Every other non-OK status —
          OVER_QUERY_LIMIT, REQUEST_DENIED, UNKNOWN_ERROR — is the lookup
          failing, and telling a professional their client's address does not
          exist because we hit a quota would be a lie with consequences.
        */
        if (status === "ZERO_RESULTS") {
          resolveAndClear({ status: "not_found" });
          return;
        }
        if (status !== "OK" || !results || results.length === 0) {
          resolveAndClear({ status: "failed" });
          return;
        }
        const point = results[0].geometry?.location;
        if (!point) {
          resolveAndClear({ status: "failed" });
          return;
        }
        resolveAndClear({ status: "located", lat: point.lat(), lng: point.lng() });
      });
    } catch {
      resolveAndClear({ status: "failed" });
    }
  });
}
